import React, { useMemo } from "react";
import DOMPurify from "dompurify";

interface HtmlPreviewPaneProps {
  html: string;
  className?: string;
  height?: string;
}

/**
 * Renders user-provided / LLM-generated HTML inside a sandboxed iframe.
 * Content is sanitized with DOMPurify (whole-document mode) before being
 * written into the iframe's srcdoc, so scripts and event handlers cannot
 * execute even if the sandbox were removed.
 */
const HtmlPreviewPane: React.FC<HtmlPreviewPaneProps> = ({
  html,
  className,
  height = "600px",
}) => {
  const sanitized = useMemo(() => {
    if (!html) return "";
    // WHOLE_DOCUMENT preserves <html>/<head>/<body>. Explicitly allow the
    // template's <link> (Google Fonts), <style>, and <meta> tags so the
    // branded preview retains its typography inside the sandboxed iframe.
    return DOMPurify.sanitize(html, {
      WHOLE_DOCUMENT: true,
      ADD_TAGS: ["link", "meta", "style"],
      ADD_ATTR: ["rel", "href", "content", "name", "charset"],
    });
  }, [html]);

  if (!html) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-900 border border-gray-700 rounded-lg text-gray-500 text-sm ${className ?? ""}`}
        style={{ height }}
      >
        No content to preview yet.
      </div>
    );
  }

  return (
    <iframe
      title="Newsletter preview"
      sandbox=""
      srcDoc={sanitized}
      className={`w-full border border-gray-700 rounded-lg ${className ?? ""}`}
      style={{ height, backgroundColor: "#0a0a0a" }}
    />
  );
};

export default HtmlPreviewPane;
